import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Divider, Input, InputNumber, Modal, Row, Select, Space, Tag, message } from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import { refineMRLine, getLookups, catalogUpdate, catalogItemPrices } from '../api';
import type { CatalogSearchResult, ItemPrices } from '../api';
import CatalogBrowserModal from './CatalogBrowserModal';

const { TextArea } = Input;

/** The subset of an MR line the refine dialog needs (MRView and POCreate both pass their line objects). */
export interface RefinableLine {
  id: number;
  lineNo: number;
  partNumber?: string | null;
  itemDescription: string;
  qty: number | string;
  unitPrice: number | string;
  unit?: { id: number; name: string } | null;
  currency?: { id: number; code: string } | null;
  vendor?: { id: number; name: string } | null;
  vendorSource?: string | null;
  lineNotes?: string | null;
  origVendorId?: number | null;
  category?: { id: number; name: string } | null;
  catalogItemId?: number | null;
  catalogItem?: { id: number; partNumber: string; description?: string; descriptionAr?: string | null; unit?: { id: number; name: string } | null } | null;
  origPartNumber?: string | null;
  origItemDescription?: string | null;
  origQty?: number | string | null;
  origUnitPrice?: number | string | null;
}

interface Props {
  line: RefinableLine | null;
  open: boolean;
  onClose: () => void;
  /** Called after a successful save; the caller reloads its data. */
  onSaved: () => void;
  /** Department of the MR (to name a standing preference). */
  departmentId?: number | null;
}

interface FormState {
  partNumber: string; itemDescription: string; qty: number; unitPrice: number;
  unitId: number | null; currencyId: number | null; vendorId: number | null;
  categoryId: number | null; catalogItemId: number | null; refinementNotes: string;
}

const mono: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600, fontSize: 13 };
const lbl = (t: string) => <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>{t}</label>;

/**
 * Procurement refinement of an approved MR line (2026-09-06 redesign): change or assign the
 * catalog item through the browser, see what the requester asked for, adopt their estimate,
 * write a missing unit back to the catalog. Reason is mandatory; the server keeps orig_* values.
 * Used from the MR view and from Create PO.
 */
const RefineLineModal: React.FC<Props> = ({ line, open, onClose, onSaved, departmentId }) => {
  const [lookups, setLookups] = useState<any>(null);
  const [form, setForm] = useState<FormState>({ partNumber: '', itemDescription: '', qty: 0, unitPrice: 0, unitId: null, currencyId: null, vendorId: null, categoryId: null, catalogItemId: null, refinementNotes: '' });
  const [picked, setPicked] = useState<CatalogSearchResult | null>(null);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingUnit, setSettingUnit] = useState(false);
  const [lineState, setLineState] = useState<RefinableLine | null>(null);
  const [prices, setPrices] = useState<ItemPrices | null>(null);

  useEffect(() => {
    if (open && !lookups) getLookups().then(setLookups).catch(() => message.error('Could not load units and vendors'));
  }, [open, lookups]);

  useEffect(() => {
    if (!open || !line) return;
    setLineState(line);
    setPicked(null);
    setForm({
      partNumber: line.partNumber || '',
      itemDescription: line.itemDescription,
      qty: Number(line.qty),
      unitPrice: Number(line.unitPrice),
      unitId: line.unit?.id || null,
      currencyId: line.currency?.id || null,
      vendorId: line.vendor?.id || null,
      categoryId: line.category?.id || null,
      catalogItemId: line.catalogItemId || null,
      refinementNotes: '',
    });
  }, [open, line]);

  // Price engine: list / last paid per vendor for the linked item
  useEffect(() => {
    if (!open || !form.catalogItemId) { setPrices(null); return; }
    catalogItemPrices(form.catalogItemId).then(setPrices).catch(() => setPrices(null));
  }, [open, form.catalogItemId]);
  const vendorPrice = prices?.vendors.find((v) => v.vendorId === form.vendorId) || null;
  const fmt = (n: number | null | undefined) => n == null ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 });
  const dmy = (d: string | null | undefined) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';

  const submit = async () => {
    if (!lineState) return;
    if (!form.refinementNotes.trim()) { message.warning('Please give a reason for the refinement'); return; }
    setSaving(true);
    try {
      await refineMRLine(lineState.id, form);
      message.success(`Line #${lineState.lineNo} refined`);
      onClose();
      onSaved();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to refine line');
    } finally {
      setSaving(false);
    }
  };

  if (!lineState) return null;
  const l = lineState;
  const wasRefined = l.origUnitPrice !== null && l.origUnitPrice !== undefined;
  const askedVendorId = wasRefined ? (l.origVendorId ?? null) : (l.vendor?.id ?? null);
  const askedVendorName = askedVendorId ? (prices?.vendors.find((v) => v.vendorId === askedVendorId)?.vendorName || lookups?.vendors?.find((v: any) => v.id === askedVendorId)?.name || l.vendor?.name || '') : '';
  const deptPref = departmentId ? prices?.deptPreferences.find((d) => d.departmentId === departmentId) : undefined;
  const sourceLabel = (vid: number | null) => vid == null ? '' : (l.vendorSource === 'requester' && vid === askedVendorId) ? "requester's ask" : (deptPref && vid === deptPref.vendorId) ? `${deptPref.departmentName}'s choice` : (l.vendorSource === 'procurement' && vid === l.vendor?.id) ? 'set by procurement' : '';
  const requester = {
    partNumber: wasRefined ? l.origPartNumber : l.partNumber,
    description: wasRefined ? l.origItemDescription : l.itemDescription,
    qty: wasRefined && l.origQty != null ? Number(l.origQty) : Number(l.qty),
    price: wasRefined ? Number(l.origUnitPrice) : Number(l.unitPrice),
  };
  const catalogUnit = picked ? picked.unit : l.catalogItem?.unit;
  const descriptionAr = picked ? picked.descriptionAr : l.catalogItem?.descriptionAr;
  const unitChanged = catalogUnit && form.unitId && catalogUnit.id !== form.unitId;
  const noCatalogUnit = !!form.catalogItemId && !catalogUnit;
  const unitLabel = (id: number | null) => lookups?.units?.find((u: any) => u.id === id)?.name ?? '';

  const setCatalogUnit = async () => {
    if (!form.catalogItemId || !form.unitId) return;
    setSettingUnit(true);
    try {
      await catalogUpdate(form.catalogItemId, { unitId: form.unitId });
      message.success(`Catalog unit set to ${unitLabel(form.unitId)}`);
      const u = lookups?.units?.find((x: any) => x.id === form.unitId);
      const unit = u ? { id: u.id, name: u.name } : null;
      if (picked) setPicked({ ...picked, unit });
      else if (l.catalogItem) setLineState({ ...l, catalogItem: { ...l.catalogItem, unit } });
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Could not update the catalog item');
    } finally {
      setSettingUnit(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <ToolOutlined />
          <span>{form.catalogItemId ? 'Refine' : 'Assign code / refine'} line #{l.lineNo}</span>
          {wasRefined && <Tag color="blue">Previously refined</Tag>}
        </Space>
      }
      open={open}
      onOk={submit}
      onCancel={onClose}
      confirmLoading={saving}
      okText="Save refinement"
      width={820}
      destroyOnClose
    >
      <Card size="small" style={{ marginBottom: 16, background: '#f6ffed', borderColor: '#b7eb8f' }} title={<span style={{ fontSize: 13 }}>Requester asked for</span>}>
        <Row gutter={16}>
          <Col span={6}><strong>Item:</strong> {requester.partNumber || <Tag color="orange">No code</Tag>}</Col>
          <Col span={10}><strong>Description:</strong> {requester.description || '-'}</Col>
          <Col span={4}><strong>Qty:</strong> {requester.qty} {l.unit?.name ?? ''}</Col>
          <Col span={4}><strong>Est. price:</strong> {requester.price > 0 ? requester.price.toFixed(2) : '—'}</Col>
        </Row>
        {(askedVendorName || l.lineNotes || deptPref) && (
          <Row gutter={16} style={{ marginTop: 6 }}>
            <Col span={12}><strong>Vendor asked:</strong> {askedVendorName ? <span>{askedVendorName} <Tag color={l.vendorSource === 'department' ? 'blue' : 'gold'} style={{ marginLeft: 4 }}>{l.vendorSource === 'department' && deptPref ? `${deptPref.departmentName}'s choice` : "requester's ask"}</Tag></span> : <span style={{ color: '#999' }}>any vendor</span>}{deptPref && deptPref.vendorId !== askedVendorId && <div style={{ fontSize: 12, color: '#666' }}>{deptPref.departmentName} usually buys from {deptPref.vendorName}{deptPref.reason ? ` — ${deptPref.reason}` : ''}</div>}</Col>
            <Col span={12}><strong>Note:</strong> {l.lineNotes || <span style={{ color: '#999' }}>—</span>}</Col>
          </Row>
        )}
      </Card>

      <Row gutter={[16, 12]}>
        <Col span={24}>
          {lbl('Item')}
          <Space wrap>
            {form.catalogItemId ? <Tag style={mono}>{form.partNumber}</Tag> : <Tag color="orange">No code</Tag>}
            <Button size="small" onClick={() => setBrowserOpen(true)}>{form.catalogItemId ? 'Change item' : 'Assign code'}</Button>
            {form.catalogItemId && <Button size="small" type="text" onClick={() => setForm((f) => ({ ...f, catalogItemId: null }))}>Unlink</Button>}
          </Space>
          {descriptionAr && <div dir="rtl" style={{ color: '#666', textAlign: 'left', marginTop: 4 }}>{descriptionAr}</div>}
        </Col>
        <Col span={24}>
          {lbl('Description')}
          <Input value={form.itemDescription} onChange={(e) => setForm((f) => ({ ...f, itemDescription: e.target.value }))} />
        </Col>
        <Col span={6}>
          {lbl((l as any).originalQty != null ? 'Qty to order again' : 'Qty')}
          <InputNumber value={form.qty} onChange={(v) => setForm((f) => ({ ...f, qty: v || 0 }))} style={{ width: '100%' }} min={0} precision={2} />
          {(l as any).originalQty != null && <div style={{ fontSize: 11, color: '#8a94a6' }}>of {Number((l as any).originalQty)} requested{(l as any).reorderFromPoId ? ` · back from ${(l as any).reorderFromPoId}` : ''}</div>}
        </Col>
        <Col span={9}>
          {lbl('Unit')}
          <Select style={{ width: '100%' }} showSearch optionFilterProp="label" placeholder="Choose unit"
            value={form.unitId ?? undefined}
            options={(lookups?.units || []).map((u: any) => ({ value: u.id, label: u.name }))}
            onChange={(v) => setForm((f) => ({ ...f, unitId: v ?? null }))} />
          {unitChanged && <div style={{ fontSize: 11, color: '#b26a00' }}>catalog default is {catalogUnit!.name}</div>}
          {noCatalogUnit && (
            <div style={{ fontSize: 11, color: '#b26a00' }}>
              no unit in catalog yet{form.unitId && <> · <a onClick={setCatalogUnit}>{settingUnit ? 'saving…' : `set ${unitLabel(form.unitId)} as catalog unit`}</a></>}
            </div>
          )}
        </Col>
        <Col span={9}>
          {lbl('Unit price')}
          <InputNumber value={form.unitPrice} onChange={(v) => setForm((f) => ({ ...f, unitPrice: v || 0 }))} style={{ width: '100%' }} min={0} precision={2} />
          <div style={{ fontSize: 11, color: '#666', lineHeight: 1.6 }}>
            {requester.price > 0 && <span>requester est. <b>{requester.price.toFixed(2)}</b> · <a onClick={() => setForm((f) => ({ ...f, unitPrice: requester.price }))}>use</a></span>}
            {vendorPrice?.listPrice != null && <span>{requester.price > 0 ? ' · ' : ''}list <b>{fmt(vendorPrice.listPrice)}</b> ({dmy(vendorPrice.listDate)}) · <a onClick={() => setForm((f) => ({ ...f, unitPrice: vendorPrice.listPrice! }))}>use</a></span>}
            {vendorPrice?.lastPaid != null && <span> · last paid <b>{fmt(vendorPrice.lastPaid)}</b> {vendorPrice.lastPo} ({dmy(vendorPrice.lastPoDate)}) · <a onClick={() => setForm((f) => ({ ...f, unitPrice: vendorPrice.lastPaid! }))}>use</a></span>}
            {form.vendorId && prices && !vendorPrice && <span>{requester.price > 0 ? ' · ' : ''}no price on record for this vendor</span>}
          </div>
        </Col>
        <Col span={6}>
          {lbl('Currency')}
          <Select style={{ width: '100%' }} value={form.currencyId ?? undefined} placeholder="Currency"
            options={(lookups?.currencies || []).map((c: any) => ({ value: c.id, label: c.code }))}
            onChange={(v) => setForm((f) => ({ ...f, currencyId: v ?? null }))} />
        </Col>
        <Col span={9}>
          {lbl('Vendor')}
          <Select style={{ width: '100%' }} showSearch allowClear optionFilterProp="label" value={form.vendorId ?? undefined} placeholder="Vendor"
            options={(lookups?.vendors || []).map((v: any) => ({ value: v.id, label: v.name }))}
            onChange={(v) => setForm((f) => ({ ...f, vendorId: v ?? null }))} />
        </Col>
        <Col span={9}>
          {lbl('Category')}
          <Select style={{ width: '100%' }} showSearch allowClear optionFilterProp="label" value={form.categoryId ?? undefined} placeholder="Category"
            options={(lookups?.categories || []).map((c: any) => ({ value: c.id, label: c.name }))}
            onChange={(v) => setForm((f) => ({ ...f, categoryId: v ?? null }))} />
        </Col>
        {prices && prices.vendors.length > 0 && (
          <Col span={24}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Vendors for this item, cheapest first · click to use that vendor and price</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
              {prices.vendors.map((v) => (
                <div key={v.vendorId} onClick={() => setForm((f) => ({ ...f, vendorId: v.vendorId, unitPrice: v.current ?? f.unitPrice }))}
                  style={{ border: `1px solid ${v.vendorId === form.vendorId ? '#1677ff' : '#e5e7eb'}`, background: v.vendorId === form.vendorId ? '#e6f4ff' : '#fafafa', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '.04em' }}>{v.vendorName}{v.appCode ? ` · ${v.appCode}` : ''}{v.preferred ? ' · preferred' : ''}{sourceLabel(v.vendorId) ? ` · ${sourceLabel(v.vendorId)}` : ''}</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{fmt(v.current)} <span style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>{v.lastPaid != null ? 'last paid' : v.listPrice != null ? 'list' : ''}</span></div>
                  <div style={{ fontSize: 11, color: '#666' }}>{v.lastPaid != null && v.listPrice != null ? `list ${fmt(v.listPrice)} · ` : ''}{v.lastPaid != null ? `${v.lastPo} ${dmy(v.lastPoDate)}` : v.listPrice != null ? `list ${dmy(v.listDate)}` : 'no price'}{v.leadTimeDays != null ? ` · lead ${v.leadTimeDays} d` : ''}</div>
                </div>
              ))}
            </div>
          </Col>
        )}
        <Col span={24}>
          <Divider style={{ margin: '8px 0' }} />
          <label style={{ fontWeight: 500, display: 'block', marginBottom: 4, color: '#cf1322' }}>Reason for refinement *</label>
          <TextArea rows={3} value={form.refinementNotes}
            onChange={(e) => setForm((f) => ({ ...f, refinementNotes: e.target.value }))}
            placeholder="Explain why you are changing these values (required)…"
            status={form.refinementNotes.trim() ? undefined : 'error'} />
        </Col>
      </Row>

      {(form.qty !== Number(l.qty) || form.unitPrice !== Number(l.unitPrice)) && (
        <div style={{ marginTop: 12, padding: 8, background: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591' }}>
          {form.qty !== Number(l.qty) && <div><strong>Qty:</strong> {Number(l.qty)} → {form.qty}</div>}
          {form.unitPrice !== Number(l.unitPrice) && <div><strong>Unit price:</strong> {Number(l.unitPrice).toFixed(2)} → {form.unitPrice.toFixed(2)}</div>}
          <div><strong>Line total:</strong> {(Number(l.qty) * Number(l.unitPrice)).toFixed(2)} → {(form.qty * form.unitPrice).toFixed(2)}</div>
        </div>
      )}

      <CatalogBrowserModal
        open={browserOpen}
        pickOne
        showPrice
        title={form.catalogItemId ? 'Change item' : 'Assign a catalog code'}
        onClose={() => setBrowserOpen(false)}
        onDone={([pick]) => {
          const item = pick.item;
          setPicked(item);
          setForm((f) => ({
            ...f,
            catalogItemId: item.id,
            partNumber: item.partNumber,
            itemDescription: item.description,
            unitId: item.unit?.id ?? f.unitId,
            unitPrice: item.defaultUnitPrice != null && Number(item.defaultUnitPrice) > 0 ? Number(item.defaultUnitPrice) : f.unitPrice,
            categoryId: item.category?.id ?? f.categoryId,
            vendorId: item.preferredVendor?.id ?? f.vendorId,
            currencyId: item.currency?.id ?? f.currencyId,
          }));
          setBrowserOpen(false);
        }}
      />
    </Modal>
  );
};

export default RefineLineModal;
